import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  FSRS_STATE_LABEL,
  getCardRetrievability,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { MorphemeInfoPanelLearningProgressStatRow } from "#morpheme-info-panel-learning-progress-stat-row";
import { useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

function formatRelative(targetMs: number, nowMs: number): string {
  const delta = targetMs - nowMs;
  const future = delta >= 0;
  const abs = Math.abs(delta);
  const suffix = future ? "from now" : "ago";
  if (abs < MINUTE_MS) {
    return future ? "now" : "just now";
  }
  if (abs < HOUR_MS) {
    return `${Math.round(abs / MINUTE_MS)} min ${suffix}`;
  }
  if (abs < DAY_MS) {
    return `${Math.round(abs / HOUR_MS)} h ${suffix}`;
  }
  if (abs < MONTH_MS) {
    return `${Math.round(abs / DAY_MS)} d ${suffix}`;
  }
  if (abs < YEAR_MS) {
    return `${Math.round(abs / MONTH_MS)} mo ${suffix}`;
  }
  return `${(abs / YEAR_MS).toFixed(1)} y ${suffix}`;
}

export function useMorphemeInfoPanelLearningProgressStatsSection({
  infoBackCardSlice,
}: {
  infoBackCardSlice: InfoMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor {
  const [isOpen, setIsOpen] = useRouteBodyState(
    "use-morpheme-info-panel-learning-progress-stats-section.tsx:isOpen",
    true,
  );
  const { data: meaningCard } = useQuery(
    actorQueries(import.meta.location).userGraphemeMeaningCard.findFirst.useQueryOptions({
      where: {
        grapheme:
          infoBackCardSlice.morphemeType === "grapheme"
            ? infoBackCardSlice.grapheme
            : infoBackCardSlice.word,
      },
      with: { userCard: { with: { fsrsCard: true } } },
    }),
  );

  const fsrsCard = useMemo(() => {
    const userCard = meaningCard?.userCard;
    if (userCard == null || userCard.fsrsCardId === null) {
      return;
    }
    return userCard.fsrsCard ?? undefined;
  }, [meaningCard]);

  const stats = useMemo(() => {
    if (fsrsCard === undefined) {
      return;
    }
    const now = new Date();
    const nowMs = now.getTime();
    const retrievability =
      fsrsCard.lastReview === null || fsrsCard.stability <= 0
        ? null
        : getCardRetrievability(fsrsCard, now);
    return {
      difficulty: fsrsCard.difficulty,
      dueMs: new Date(fsrsCard.due).getTime(),
      lapses: fsrsCard.lapses,
      lastReviewMs: fsrsCard.lastReview === null ? null : new Date(fsrsCard.lastReview).getTime(),
      nowMs,
      reps: fsrsCard.reps,
      retrievability,
      stability: fsrsCard.stability,
      state: fsrsCard.state,
    };
  }, [fsrsCard]);

  return {
    bodyKey: "morpheme-info-learning-progress-stats-body",
    headerKey: "morpheme-info-learning-progress-stats-header",
    iconName: "LuLibrary",
    isOpen,
    onToggle: () => setIsOpen((previousState) => !previousState),
    renderBody: () => {
      if (stats === undefined) {
        return (
          <View className="px-4 pt-2 pb-4">
            <Text className="text-sm text-muted-foreground">Not studied yet.</Text>
          </View>
        );
      }
      const retrievabilityLabel =
        stats.retrievability === null ? "—" : `${Math.round(stats.retrievability * 100)}%`;
      return (
        <View className="gap-2 px-4 pt-2 pb-4">
          <View className="flex-row items-center justify-between">
            <Text className="text-base font-semibold">
              {FSRS_STATE_LABEL[stats.state] ?? "Unknown"}
            </Text>
            <Text className="text-base font-semibold text-foreground">{retrievabilityLabel}</Text>
          </View>
          <Text className="text-xs text-muted-foreground">
            Current state and probability of recall right now.
          </Text>
          <View className="mt-2 gap-1.5">
            <MorphemeInfoPanelLearningProgressStatRow
              label="Stability"
              value={`${stats.stability.toFixed(2)} d`}
            />
            <MorphemeInfoPanelLearningProgressStatRow
              label="Difficulty"
              value={`${stats.difficulty.toFixed(2)} / 10`}
            />
            <MorphemeInfoPanelLearningProgressStatRow label="Reviews" value={String(stats.reps)} />
            <MorphemeInfoPanelLearningProgressStatRow label="Lapses" value={String(stats.lapses)} />
            <MorphemeInfoPanelLearningProgressStatRow
              label="Last review"
              value={
                stats.lastReviewMs === null ? "—" : formatRelative(stats.lastReviewMs, stats.nowMs)
              }
            />
            <MorphemeInfoPanelLearningProgressStatRow
              label="Next due"
              value={formatRelative(stats.dueMs, stats.nowMs)}
            />
          </View>
        </View>
      );
    },
    title: "Progress",
  };
}
